/**
 * Acumulación de la evidencia observada (004 · US1 · T029; FR-021, FR-022; research R-6).
 *
 * **Función pura**, sin timers ni DOM: recibe lo que leyó la cámara y devuelve la colección nueva.
 *
 * Reglas, y el porqué de cada una:
 *
 * - **Únicos por índice y estrictamente crecientes.** El servidor exige que los índices crezcan en
 *   orden de captura (FR-022); un índice repetido o menor que el último ya visto se **descarta**,
 *   porque incluirlo haría fallar toda la evidencia en vez de solo ese frame.
 * - **Instantes relativos.** `dt_ms` cuenta desde el **primer** frame observado, con
 *   `performance.now()` (R-6): el reloj de pared del teléfono es telemetría, nunca verdad. El
 *   primero lleva siempre `dt_ms = 0`.
 * - **Revalidación estricta del alfabeto.** `decodeFerminQr` acepta el superconjunto `[0-9A-Z]` del
 *   modo alfanumérico de QR, no crockford32: un `FM1C` con `I`, `L`, `O` o `U` decodifica pero no
 *   es un token válido. Aquí se vuelve a validar con `ChallengeShortIdSchema` y
 *   `ChallengeTokenSchema` antes de guardar nada (wiring §1.6).
 * - **Frames de otro challenge fuera.** Solo entran los que llevan el `short_id` del challenge en
 *   curso.
 */
import {
  ChallengeShortIdSchema,
  ChallengeTokenSchema,
  decodeFerminQr,
  type ObservedFrame,
} from '@fermin/contracts';

/** Tope de FR-021: la evidencia sale a los 2.5 s del primer frame aunque falten índices. */
export const EVIDENCE_DEADLINE_MS = 2_500;

/** Tope del contrato para `dt_ms`. */
const MAX_DT_MS = 30_000;

export interface CollectedFrame {
  i: number;
  t: string;
  /** Milisegundos desde el primer frame observado. */
  dtMs: number;
}

export interface FrameCollection {
  /** En orden de captura, únicos por índice y con índice estrictamente creciente. */
  frames: CollectedFrame[];
  /** Instante monotónico del primer frame; `null` mientras no hay ninguno. */
  firstAtMs: number | null;
  /** Mayor índice ya aceptado; `0` mientras no hay ninguno. */
  maxIndex: number;
}

export interface ChallengeShape {
  /** Campo `c` del `FM1C` del challenge en curso. */
  shortId: string;
  /** Frames que el marcador va a pintar (4–10). */
  frameCount: number;
  /** Mínimo de índices distintos que exige el servidor (3–10). */
  requiredFrames: number;
}

export function emptyCollection(): FrameCollection {
  return { frames: [], firstAtMs: null, maxIndex: 0 };
}

/** Un frame es de la "mitad final" si su índice supera la mitad del total (FR-022). */
export function isLateHalf(index: number, frameCount: number): boolean {
  return index > frameCount / 2;
}

/**
 * Incorpora una lectura. Devuelve **la misma colección** (identidad estable) cuando no aporta nada:
 * texto ajeno, otro challenge, índice fuera de rango, repetido o fuera de orden.
 */
export function collectFrame(
  collection: FrameCollection,
  read: { text: string; capturedAtMs: number },
  challenge: ChallengeShape,
): FrameCollection {
  const decoded = decodeFerminQr(read.text);
  if (decoded === null || decoded.kind !== 'challenge') {
    return collection;
  }
  if (!ChallengeShortIdSchema.safeParse(decoded.c).success) {
    return collection;
  }
  if (!ChallengeTokenSchema.safeParse(decoded.t).success) {
    return collection;
  }
  if (decoded.c !== challenge.shortId) {
    return collection;
  }
  if (decoded.i < 1 || decoded.i > challenge.frameCount) {
    return collection;
  }
  if (decoded.i <= collection.maxIndex) {
    // Repetido o fuera de orden: incluirlo rompería el orden estrictamente creciente de FR-022.
    return collection;
  }

  const firstAtMs = collection.firstAtMs ?? read.capturedAtMs;
  const dtMs = Math.min(MAX_DT_MS, Math.max(0, Math.round(read.capturedAtMs - firstAtMs)));
  return {
    frames: [...collection.frames, { i: decoded.i, t: decoded.t, dtMs }],
    firstAtMs,
    maxIndex: decoded.i,
  };
}

/** Motivo por el que la evidencia ya puede enviarse (solo diagnóstico interno). */
export type EvidenceTrigger = 'ENOUGH_FRAMES' | 'SEQUENCE_END' | 'DEADLINE';

/**
 * FR-021: la evidencia sale cuando se tienen los frames requeridos **en orden y con uno de la
 * mitad final** —lo mínimo que el servidor aceptará—, al detectar el fin de la secuencia, o a los
 * 2.5 s del primero, lo que ocurra antes.
 */
export function evidenceTrigger(
  collection: FrameCollection,
  challenge: ChallengeShape,
  nowMs: number,
  deadlineMs: number = EVIDENCE_DEADLINE_MS,
): EvidenceTrigger | null {
  if (collection.frames.length === 0 || collection.firstAtMs === null) {
    return null;
  }
  const hasLateHalf = collection.frames.some((frame) =>
    isLateHalf(frame.i, challenge.frameCount),
  );
  if (collection.frames.length >= challenge.requiredFrames && hasLateHalf) {
    return 'ENOUGH_FRAMES';
  }
  if (collection.maxIndex >= challenge.frameCount) {
    return 'SEQUENCE_END';
  }
  if (nowMs - collection.firstAtMs >= deadlineMs) {
    return 'DEADLINE';
  }
  return null;
}

/** Cuerpo de `POST /challenges/{id}/verify`: los frames tal cual se observaron. */
export function toObservedFrames(collection: FrameCollection): ObservedFrame[] {
  return collection.frames.map((frame) => ({ i: frame.i, t: frame.t, dt_ms: frame.dtMs }));
}

/** Avance 0–100 para el anillo de progreso: frames observados sobre los requeridos. */
export function evidenceProgress(
  collection: FrameCollection,
  challenge: ChallengeShape,
): number {
  if (challenge.requiredFrames <= 0) {
    return 0;
  }
  const ratio = collection.frames.length / challenge.requiredFrames;
  return Math.max(0, Math.min(100, Math.round(ratio * 100)));
}
